import type { BasePagingResponse, BaseResponse } from "../../../../lib/api-contract";
import { apiUtils } from "../../../../utils/apiUtils";
import {
  mapRawApiResponseToApiResponse,
  MerchantCouponItemApi,
  MerchantCouponListApiRequest,
  MerchantCouponListRawApiResponse,
  MerchantCouponStatisticRawApiResponse,
  MerchantRewardSummaryApi,
} from "../data/merchantCouponList.data";

interface MerchantCouponRestockResponse {
  id: string;
  quantity: number;
  remaining_quantity: number;
}

export const getMerchantCouponList = async (
  request: MerchantCouponListApiRequest,
  signal?: AbortSignal,
): Promise<BasePagingResponse<MerchantCouponItemApi>> => {
  const raw = await apiUtils.postData<
    MerchantCouponListRawApiResponse,
    MerchantCouponListApiRequest
  >(
    "/v1/merchant/local-merchant-coupon/list",
    request,
    "Failed to fetch merchant coupons",
    { signal },
  );

  return {
    paging: raw.paging ?? {
      count: 0,
      index: request.pagination.index,
      size: request.pagination.size,
      total: 0,
    },
    list: (raw.list ?? []).map(mapRawApiResponseToApiResponse),
  };
};

export const getMerchantRewardSummary = async (
  signal?: AbortSignal,
): Promise<MerchantRewardSummaryApi> => {
  const rawData = await apiUtils.getData<MerchantCouponStatisticRawApiResponse>(
    "/v1/merchant/local-merchant-coupon/statistic",
    "Failed to fetch merchant reward summary",
    { signal },
  );

  return {
    total_active_coupons: rawData.total_active_coupons ?? 0,
    total_coupons: rawData.total_coupons ?? 0,
    total_viewed_coupons: rawData.total_viewed_coupons ?? 0,
    coupons_redeemed: rawData.coupons_redeemed ?? 0,
    usage_count: rawData.usage_count ?? 0,
    low_or_out_of_stock: rawData.low_or_out_of_stock ?? 0,
  };
};

export const deleteMerchantCoupon = async (
  id: string,
  signal?: AbortSignal,
): Promise<BaseResponse<null>> => {
  return apiUtils.delete<null>(`/v1/merchant/local-merchant-coupon/${id}`, {
    signal,
  });
};

export const restockMerchantCoupon = async (
  couponId: string,
  restockQuantity: number,
  signal?: AbortSignal,
): Promise<BaseResponse<MerchantCouponRestockResponse>> => {
  const query = new URLSearchParams({ quantity: String(restockQuantity) });

  return apiUtils.patch<MerchantCouponRestockResponse>(
    `/v1/merchant/local-merchant-coupon/${encodeURIComponent(couponId)}/restock?${query.toString()}`,
    undefined,
    { signal },
  );
};
